// src/hooks/useUtilizationPeriods.js
import { useState, useEffect, useMemo } from 'react';
import { Logger } from '../utils/logger';

const logger = new Logger('useUtilizationPeriods');

/**
 * Custom hook for fetching utilization data across multiple time periods
 * Handles parallel fetching and period selection
 *
 * @param {string} baseUrl - Base API URL
 * @param {string} employeeId - Employee identifier
 * @param {boolean} open - Whether modal is open (triggers fetch)
 * @returns {Object} { allPeriodsData, selectedPeriod, setSelectedPeriod, loading, error }
 *
 * @example
 * const { allPeriodsData, selectedPeriod, setSelectedPeriod, loading } = useUtilizationPeriods(
 *   API_BASE_URL,
 *   employeeId,
 *   open
 * );
 */
export const useUtilizationPeriods = (baseUrl, employeeId, open) => {
  const [selectedPeriod, setSelectedPeriod] = useState('13w');
  const [allPeriodsData, setAllPeriodsData] = useState({
    '1w': null,
    '4w': null,
    '13w': null,
    '26w': null,
    'ytd': null,
    'all': null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Period configuration
  const periods = useMemo(() => ['1w', '4w', '13w', '26w', 'ytd', 'all'], []);

  useEffect(() => {
    if (!open || !employeeId) return;

    const fetchAllPeriods = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch all periods in parallel
        const requests = periods.map(period =>
          fetch(`${baseUrl}/employees/${employeeId}/utilization-details?period=${period}`)
            .then(response => {
              if (!response.ok) {
                throw new Error(`HTTP error ${response.status}`);
              }
              return response.json();
            })
            .then(data => ({ period, data }))
            .catch(err => {
              logger.error(`Error fetching period ${period}:`, err);
              return { period, data: null, error: err.message };
            })
        );

        const results = await Promise.all(requests);

        // Build data object for all periods
        const periodsData = {};
        results.forEach(({ period, data }) => {
          periodsData[period] = data;
        });

        setAllPeriodsData(periodsData);
        logger.debug('All periods fetched successfully');
      } catch (err) {
        logger.error('Error fetching utilization periods:', err);
        setError(`Failed to load utilization data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchAllPeriods();
  }, [baseUrl, employeeId, open, periods]);

  // Get currently selected period data
  const currentPeriodData = useMemo(() => {
    return allPeriodsData[selectedPeriod] || {
      average: null,
      count: 0,
      trend: 'neutral',
      weekly_data: [],
      period: 'Last 13 weeks',
      unique_weeks: 0,
      target: 40,
      last_updated: null
    };
  }, [allPeriodsData, selectedPeriod]);

  return {
    allPeriodsData,
    selectedPeriod,
    setSelectedPeriod,
    currentPeriodData,
    loading,
    error
  };
};

/**
 * Period configuration for UI display
 */
export const PERIOD_OPTIONS = [
  { value: '1w', label: 'Last Week' },
  { value: '4w', label: 'Last 4 Weeks' },
  { value: '13w', label: 'Last 13 Weeks' },
  { value: '26w', label: 'Last 26 Weeks' },
  { value: 'ytd', label: 'Year to Date' },
  { value: 'all', label: 'All Time' }
];
